import { useEffect, useState } from "react";
import { CometChat } from "@cometchat-pro/chat";
import { useDispatch, useSelector } from "react-redux";
import useActiveUser from "./useActiveUser";
import {
    initializeCometChat,
    removeCometData,
    updateUserState,
    updateActiveCometUser,
    updateActiveCometUserData,
    updateCoverWindowChat,
} from "../redux/cometChat/cometChatActions";
import { updateChatWithReceiver } from "../redux/cometChat/cometChatActions";
import { USER_FILES_URL } from '../api_endpoints/API_URL';
import {
    cometCreateUser,
    cometUserExists,
    cometAuthToken,
    cometLoggedInUser,
    cometLoginUser,
    cometUserUpdate,
    cometMessageListener,
    cometLogoutUser
} from "../helper_functions/CometChatFunctions";
import { showToast } from "../redux/common/commonActions";
import { bothPanelActiveAs } from "../redux/user/userActions";

const useCometChat = () => {
    const dispatch = useDispatch();
    const {
        isLoggedIn,
        userRole,
        userData,
        userID,
        otp_verified,
        email_verified
    } = useSelector((state) => state.userState);
    const cometChatState = useSelector((state) => state.cometChatState);
    const activeUser = useActiveUser();
    const hostname = window?.location?.hostname

    //constant definition
    const activeRole = activeUser ?? userRole
    const isActiveId = userData?.display_user_id && userData.display_user_id.includes('/') ?
        userData.display_user_id.split('/') : undefined
    const userDisplayId = isActiveId ? activeUser && activeRole === 'Client' ? isActiveId[0] : isActiveId[1] : userData?.display_user_id;
    const uIdConcat = userDisplayId && userDisplayId?.toLowerCase() + '_' + userID;
    const uidType = uIdConcat && uIdConcat?.toString()
    const uniqueIdentifier = hostname === 'localhost' ? uidType + '_dev' : uidType
    const roleId = hostname === 'localhost' ? uniqueIdentifier && uniqueIdentifier?.substring(0, 2) + '_dev' : uniqueIdentifier && uniqueIdentifier?.substring(0, 2)

    //states
    const [loginInProgress, setLoginInProgress] = useState(false);
    const [disableFetchReq, setDisableFetchReq] = useState(false);
    const [roleChangedChatContext, setRoleChangedChatContext] = useState(false);

    const updateCometUser = async (uid) => {
        let cometUserData = {
            uid: uid,
            name: userDisplayId,
            roles: roleId,
        };

        if (userData?.user_avatar && userData.user_avatar !== '' && userData.user_avatar.trim().length > 0) {
            const newAvatarUrl = USER_FILES_URL + userData.user_avatar
            cometUserData = { ...cometUserData, avatar: newAvatarUrl }
        }
        const updateRes = await cometUserUpdate(cometUserData)
    }

    const handleCometChatMessageStates = async (uid) => {
        const messageListenerRes = await cometMessageListener(uid)
    }

    const handleCometLoginUpdates = (uid, userData) => {
        updateCometUser(uid)
        handleCometChatMessageStates(uid)
        dispatch(updateUserState(2))
        dispatch(updateActiveCometUser(uid))
        dispatch(updateActiveCometUserData(userData))
        setDisableFetchReq(true)
    }

    const fetchAuthToken = async (userData) => {
        if (disableFetchReq === false && userData?.authToken?.length > 0) {
            const fetchTokenRes = await cometAuthToken(userData.uid, userData.authToken)
            if (fetchTokenRes?.status === true) {
                const getLoggedInUserRes = await cometLoggedInUser(userData.uid)
                if (getLoggedInUserRes?.status === true) {
                    handleCometLoginUpdates(userData.uid, getLoggedInUserRes.data)
                } else {
                    if (getLoggedInUserRes?.type === 'noUser') {
                        window.location.reload()
                    } else {
                        cometUserLogin(userData.uid)
                    }
                }
            } else {
                //Trigger reload to update authToken
                window.location.reload()
            }
        }
    }

    const cometUserLogin = async (uid) => {
        const cometLoginUserRes = await cometLoginUser(uid)
        if (cometLoginUserRes?.status === true) {
            fetchAuthToken(cometLoginUserRes.data)
            setLoginInProgress(false);
        } else {
            if (cometLoginUserRes?.type === 'notInitialized') {
                dispatch(initializeCometChat())
                setLoginInProgress(false);
            } else setLoginInProgress(false);
        }
    }

    const cometUserLoginProcess = async (uid) => {
        if (loginInProgress) {
            // Login request already in progress. Please wait.
            return;
        }
        setLoginInProgress(true);

        const getLoggedInUserRes = await cometLoggedInUser(uid)
        if (getLoggedInUserRes?.status === true) {
            setLoginInProgress(false);
            fetchAuthToken(getLoggedInUserRes.data);
        } else {
            cometUserLogin(uid);
        }
    };

    const createCometUser = async (uid, credentialData) => {
        const ucd = {
            uid: credentialData?.uid ?? uid,
            userDisplayId: credentialData?.userDisplayId ?? userDisplayId,
            roleId: credentialData?.roleId ?? roleId
        } //user creation data

        let user = new CometChat.User(ucd?.uid);
        user.setName(ucd?.userDisplayId);
        user.setRole(ucd?.roleId);

        user.setMetadata({
            role: ucd?.roleId,
            contactList: [],
            allOrderAccepted: [],
            "@private":
            {
                "email": userData?.email
            }

        });

        if (userData?.user_avatar && userData.user_avatar !== '' && userData.user_avatar.trim().length > 0) {
            const avatarUrl = USER_FILES_URL + userData.user_avatar
            user.setAvatar(avatarUrl)
        }
        // Check if the user exists
        const userExistsRes = await cometUserExists(ucd?.uid)
        if (userExistsRes?.status === true) {
            if (credentialData && Object.keys(credentialData)?.length) {
                return;
            }
            cometUserLoginProcess(ucd?.uid);
        } else {
            if (userExistsRes?.type === 'createNewUser') {
                const createNewUserRes = await cometCreateUser(user, ucd?.uid)
                if (createNewUserRes?.status === true || createNewUserRes?.type === 'userAlreadyExists') {
                    if (credentialData && Object.keys(credentialData)?.length) {
                        return;
                    }
                    cometUserLoginProcess(ucd?.uid);
                }
            }
        }
    };

    const checkUserRole = () => {
        const isUserLoggedInActive = isLoggedIn === true && userRole === "Client" || userRole === "Expert" || userRole === "Client/Expert" && otp_verified == 2 && email_verified == 2
        if (isUserLoggedInActive) {
            //Sometimes activeRole is Client/Expert : stay clear from the error state for creating comet users
            if (uniqueIdentifier !== undefined && userDisplayId !== 'client_id' && userDisplayId !== 'expert_id' && (activeRole === 'Client' || activeRole === 'Expert')) {
                createCometUser(uniqueIdentifier)
                isActiveId?.forEach((scriptedDisplayId) => {
                    if (!uniqueIdentifier?.toLowerCase()?.includes(scriptedDisplayId?.toLowerCase())) {
                        const scriptedDetails = {
                            uid: scriptedDisplayId?.toLowerCase() + '_' + userID + (hostname === 'localhost' ? '_dev' : ''),
                            userDisplayId: scriptedDisplayId,
                            roleId: scriptedDisplayId?.toLowerCase().substring(0, 2) + (hostname === 'localhost' ? '_dev' : '')
                        }
                        setTimeout(() => {
                            createCometUser(uniqueIdentifier, scriptedDetails)
                        }, 0)
                    }
                })
            }
            // else {
            //     //Something really wrong happened 
            // };
        }
        // else {
        //User role not present
        // }
    }

    const handleCometChatLogout = async () => {
        if (userRole === "Client" || userRole === "Expert" || userRole === "Client/Expert" || userRole !== '') {
            const logoutRes = await cometLogoutUser()
            setTimeout(() => {
                dispatch(removeCometData())
                localStorage.clear();
            }, 100)
        }

        const alreadyLoaded = localStorage.getItem("isCometError");
        if (alreadyLoaded) {
            localStorage.removeItem("isCometError");
            setTimeout(() => {
                dispatch(removeCometData())
                localStorage.clear();
            }, 100)
        }
        if (disableFetchReq) {
            setDisableFetchReq(false)
        }
    }

    useEffect(() => {
        setDisableFetchReq(false);
        if (isLoggedIn) {
            dispatch(initializeCometChat());
        } else {
            handleCometChatLogout();
        }
    }, [isLoggedIn, activeRole, uniqueIdentifier, userID]);

    useEffect(() => {
        let intervalId;
        let counter = 0;
        const referenceUser = cometChatState?.orderIdContext?.newMetaDataRefObj?.to_uid?.split('_')[1] ?? false
        const isSameUser = userID.toString() === referenceUser
        if (
            isLoggedIn &&
            (cometChatState?.cometChatUserState === 2 || roleChangedChatContext) &&
            cometChatState.isNotLoggedInContext !== 0 &&
            cometChatState.chatReceiverUid !== undefined &&
            activeRole !== undefined && !isSameUser
        ) {
            if ((activeUser === 'Client' && cometChatState.isNotLoggedInContext === 2) ||
                (activeUser === 'Expert' && cometChatState.isNotLoggedInContext === 1)) {
                intervalId = setInterval(() => {
                    const metaDataForChat = cometChatState.orderIdContext?.newMetaDataRefObj ?? {}
                    dispatch(updateChatWithReceiver(
                        cometChatState.chatReceiverUid,
                        metaDataForChat
                    ));
                    counter++;
                    // Clear the interval if the maximum count is reached
                    if (counter >= 30) {
                        clearInterval(intervalId);
                    }
                }, 3000)
            } else {
                if (userRole === 'Client/Expert') {
                    const newRole = cometChatState?.isNotLoggedInContext === 1 ?
                        'Expert' : cometChatState?.isNotLoggedInContext === 2 ?
                            'Client' : 'none'
                    if (newRole !== 'none') {
                        dispatch(bothPanelActiveAs(newRole))
                        setRoleChangedChatContext(true)
                    } else {
                        setRoleChangedChatContext(false)
                    }
                } else {
                    setRoleChangedChatContext(false)
                    dispatch(showToast({ show: true, msg: 'Wrong Role selected for chat' }));
                    dispatch(updateChatWithReceiver(undefined));
                    dispatch(updateCoverWindowChat(0))
                    setTimeout(() => {
                        dispatch(showToast({ show: false, msg: '' }));
                    }, 3500)
                }
            }
        }

        if (cometChatState?.cometChatUserState && cometChatState.cometChatUserState === 1) {
            checkUserRole();
        }

        return () => {
            clearInterval(intervalId);
        };
    }, [cometChatState, isLoggedIn, otp_verified, email_verified]);

    return {
        updateCometUser,
    };
};

export default useCometChat;
